import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getPagamentosControllerObterPrecoQueryKey,
  usePagamentosControllerDefinirPreco,
} from '@/api/generated/discussões-técnicas-pagamento/discussões-técnicas-pagamento';
import type { PrecoDiscussaoResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { formatarCentavos } from '@/features/discussoes/rotulos';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';

/** "69,90" ou "69.90" → 6990. Aceita vírgula (padrão BR) e ponto. */
function paraCentavos(valor: string): number {
  const normalizado = valor.replace(/\./g, '').replace(',', '.');
  const numero = Number(normalizado);
  return Math.round(numero * 100);
}

const schema = z.object({
  valorApp: z
    .string()
    .trim()
    .min(1, 'Informe o valor do aplicativo')
    .refine((v) => !Number.isNaN(paraCentavos(v)) && paraCentavos(v) > 0, 'Informe um valor válido'),
  percentualDescontoWeb: z.coerce.number().min(0, 'Não pode ser negativo').max(100, 'No máximo 100%'),
});
type Formulario = z.input<typeof schema>;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  atual: PrecoDiscussaoResponse | undefined;
}

/**
 * Preço é sempre digitado como App × desconto do Web — o preço do Web nunca
 * é digitado direto, é `valorApp × (1 − percentual/100)`, calculado ao vivo
 * aqui e de novo no servidor.
 */
export function PrecoDiscussaoFormDialogo({ aberto, aoFechar, atual }: Props) {
  const queryClient = useQueryClient();
  const definirPreco = usePagamentosControllerDefinirPreco();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(schema),
    defaultValues: { valorApp: '', percentualDescontoWeb: 0 },
  });

  useEffect(() => {
    if (!aberto) return;
    reset(
      atual
        ? {
            valorApp: (atual.valorAppCentavos / 100).toFixed(2).replace('.', ','),
            percentualDescontoWeb: atual.percentualDescontoWeb,
          }
        : { valorApp: '', percentualDescontoWeb: 0 },
    );
  }, [aberto, atual, reset]);

  const valorAppDigitado = watch('valorApp');
  const percentualDigitado = Number(watch('percentualDescontoWeb')) || 0;
  const previaWebCentavos =
    valorAppDigitado && !Number.isNaN(paraCentavos(valorAppDigitado))
      ? Math.round(paraCentavos(valorAppDigitado) * (1 - percentualDigitado / 100))
      : null;

  const aoEnviar = handleSubmit(async (bruto) => {
    const dados = schema.parse(bruto);

    try {
      await definirPreco.mutateAsync({
        data: {
          valorAppCentavos: paraCentavos(dados.valorApp),
          percentualDescontoWeb: dados.percentualDescontoWeb,
        },
      });
      await queryClient.invalidateQueries({ queryKey: getPagamentosControllerObterPrecoQueryKey() });
      toast.warning('Novo preço configurado.', {
        description:
          'Avise o time de desenvolvimento sobre essa mudança — o valor também precisa ser atualizado nas lojas de aplicativo (App Store / Google Play).',
        duration: 10000,
      });
      aoFechar();
    } catch (erro) {
      setError('root', {
        message: erro instanceof ApiError ? erro.message : 'Não foi possível salvar o preço.',
      });
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Definir novo preço</DialogTitle>
          <DialogDescription>
            Append-only: salvar aqui cria uma configuração nova, não sobrescreve a anterior.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void aoEnviar(e)} className="space-y-4" noValidate>
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo
            id="valorApp"
            rotulo="Valor no aplicativo (R$)"
            obrigatorio
            erro={errors.valorApp?.message}
            dica="Ex.: 69,90"
          >
            <Input
              id="valorApp"
              inputMode="decimal"
              placeholder="69,90"
              className={CLASSE_CONTROLE}
              {...register('valorApp')}
            />
          </Campo>

          <Campo
            id="percentualDescontoWeb"
            rotulo="Desconto no Web (%)"
            obrigatorio
            erro={errors.percentualDescontoWeb?.message}
            dica="Ex.: 15 — o Web sai 15% mais barato que o App."
          >
            <Input
              id="percentualDescontoWeb"
              type="number"
              step="0.01"
              min={0}
              max={100}
              className={CLASSE_CONTROLE}
              {...register('percentualDescontoWeb')}
            />
          </Campo>

          {previaWebCentavos !== null && (
            <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
              <span className="text-muted-foreground">Preço do Web resultante: </span>
              <span className="font-medium text-foreground">{formatarCentavos(previaWebCentavos)}</span>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar novo preço
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
